/**
 * Global Error Handler Component
 * 
 * Catches and handles unhandled errors globally:
 * - JavaScript runtime errors
 * - Unhandled promise rejections
 * - Network errors
 * - API errors
 */

import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotifications } from '../feedback/NotificationSystem';
import { useErrorHandler } from '../../hooks/useErrorHandler';
import { isModuleLoadError, isReactErrorBoundaryHandling } from '../../utils/moduleLoadError';

interface GlobalErrorHandlerProps {
  children: React.ReactNode;
}

export const GlobalErrorHandler: React.FC<GlobalErrorHandlerProps> = ({ children }) => {
  const { t } = useTranslation('notifications');
  const { showSuccess, showError } = useNotifications();
  const { handleError } = useErrorHandler();

  useEffect(() => {
    // Handle unhandled JavaScript errors
    const handleGlobalError = (event: ErrorEvent) => {
      if (isReactErrorBoundaryHandling() || isModuleLoadError(event.error ?? event.message)) {
        event.preventDefault();
        return;
      }

      console.error('Global error caught:', event.error);
      
      handleError(event.error, {
        context: 'global_javascript_error',
        showNotification: true,
        logError: true
      });

      event.preventDefault();
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (isReactErrorBoundaryHandling() || isModuleLoadError(event.reason)) {
        event.preventDefault();
        return;
      }

      console.error('Unhandled promise rejection:', event.reason);
      
      handleError(event.reason, {
        context: 'unhandled_promise_rejection',
        showNotification: true,
        logError: true
      });

      event.preventDefault();
    };

    // Handle network status changes
    const handleOnline = () => {
      showSuccess(t('connection.restored'), {
        title: t('connection.backOnline'),
        persistent: false,
        duration: 3000
      });
    };

    const handleOffline = () => {
      showError(t('connection.lost'), {
        title: t('connection.offline'),
        persistent: true,
        actions: [
          {
            label: t('connection.checkConnection'),
            action: () => {
              if (navigator.onLine) {
                window.location.reload();
              }
            },
            style: 'primary'
          }
        ]
      });
    };

    // Add event listeners
    window.addEventListener('error', handleGlobalError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Cleanup event listeners
    return () => {
      window.removeEventListener('error', handleGlobalError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [handleError, showError, showSuccess, t]);

  return <>{children}</>;
};

export default GlobalErrorHandler;